import { useEffect, useState } from "react";
import { Send } from "lucide-react";
import { api } from "@/lib/api";
import { Switch } from "@/components/ui/switch";
import { SingleImageUpload, MultiImageUpload } from "@/components/ImageUpload";
import { DEFAULT_CONTENT, fmtDate, fmtTime } from "@/lib/community";
import { useAdminData, adminAct, Field, NSelect, inputCls, Badge, Panel, btnDark } from "./shared";
import { VideoUpload } from "./VideoUpload";

export function EmailsTab() {
  const [log, reload] = useAdminData("/admin/community/notifications");
  const [f, setF] = useState({ subject: "", heading: "", message: "", audience: "members", essential: false });
  const [busy, setBusy] = useState(false);
  const send = async () => {
    if (!window.confirm(`Send "${f.subject}" to ${f.audience === "prive" ? "all Privé members" : "all Jenova Girls"}?`)) return;
    setBusy(true);
    const r = await adminAct(() => api.post("/admin/community/email/update", f));
    setBusy(false);
    if (r) {
      window.alert(`Sending to ${r.recipients} member(s).`);
      setF({ ...f, subject: "", heading: "", message: "" });
      setTimeout(reload, 2500);
    }
  };
  return (
    <div className="space-y-6" data-testid="admin-emails-tab">
      <Panel title="Send a community update">
        <div className="grid md:grid-cols-2 gap-4">
          <Field label="Email subject"><input className={inputCls} value={f.subject} onChange={(e) => setF({ ...f, subject: e.target.value })} data-testid="update-subject-input" /></Field>
          <Field label="Heading in the email"><input className={inputCls} value={f.heading} onChange={(e) => setF({ ...f, heading: e.target.value })} data-testid="update-heading-input" /></Field>
        </div>
        <Field label="Message" className="mt-4"><textarea className={`${inputCls} min-h-[140px]`} value={f.message} onChange={(e) => setF({ ...f, message: e.target.value })} data-testid="update-message-input" /></Field>
        <div className="flex flex-wrap items-center gap-6 mt-4">
          <div className="w-56"><NSelect value={f.audience} onChange={(v) => setF({ ...f, audience: v })} options={[{ value: "members", label: "All Jenova Girls" }, { value: "prive", label: "Privé members" }]} testId="update-audience-select" /></div>
          <label className="flex items-center gap-2 text-sm"><Switch checked={f.essential} onCheckedChange={(v) => setF({ ...f, essential: v })} data-testid="update-essential-switch" /> Important/essential (sends even to members who opted out of updates)</label>
          <button className={btnDark} disabled={busy || !f.subject || !f.heading || !f.message} onClick={send} data-testid="update-send-btn"><Send className="w-3.5 h-3.5" /> Send</button>
        </div>
      </Panel>
      <Panel title="Email history">
        <p className="text-xs text-[#7A6A62] mb-3">Transactional emails (RSVPs, reminders, decisions) always send. Marketing emails (bulletins, updates) respect each member's preferences and include an unsubscribe link.</p>
        <div className="overflow-x-auto max-h-[480px] overflow-y-auto">
          <table className="w-full text-sm">
            <thead><tr className="text-left text-[10px] uppercase tracking-[0.18em] text-[#7A6A62]"><th className="py-2">When</th><th>To</th><th>Subject</th><th>Type</th><th>Status</th></tr></thead>
            <tbody>
              {(log || []).map((n) => (
                <tr key={n.id} className="border-t border-[#F0E8DD]">
                  <td className="py-2 text-xs whitespace-nowrap">{fmtDate(n.created_at, { month: "short", day: "numeric" })} {fmtTime(n.created_at)}</td>
                  <td className="text-xs">{n.to}</td><td className="text-xs">{n.subject}</td>
                  <td className="text-xs text-[#7A6A62]">{n.type} · {n.category}</td><td><Badge s={n.status === "skipped_pref" ? "draft" : n.status} /></td>
                </tr>
              ))}
            </tbody>
          </table>
          {log && !log.length && <p className="text-sm text-[#7A6A62] py-4">No community emails sent yet.</p>}
        </div>
      </Panel>
    </div>
  );
}

export function ContentTab() {
  const [data] = useAdminData("/community/content");
  const [f, setF] = useState(DEFAULT_CONTENT);
  useEffect(() => {
    if (data) setF({ ...DEFAULT_CONTENT, ...data });
  }, [data]);
  const set = (k) => (v) => setF((x) => ({ ...x, [k]: v?.target ? v.target.value : v }));
  const save = () => adminAct(() => api.put("/admin/community/content", f), "Experience page updated");
  const t = (k, label, area) => (
    <Field label={label}>
      {area ? <textarea className={`${inputCls} min-h-[100px]`} value={f[k] || ""} onChange={set(k)} data-testid={`content-${k}`} />
        : <input className={inputCls} value={f[k] || ""} onChange={set(k)} data-testid={`content-${k}`} />}
    </Field>
  );
  return (
    <Panel title="The Experience page" testId="admin-content-tab" action={<button className={btnDark} onClick={save} data-testid="content-save-btn">Save changes</button>}>
      <div className="grid md:grid-cols-2 gap-8">
        <div className="space-y-4">
          {t("hero_headline", "Headline")}
          {t("hero_description", "Hero description", true)}
          <Field label="Hero photo (leave empty for the default)"><SingleImageUpload value={f.hero_image} onChange={set("hero_image")} testId="content-hero-image" aspectRatio="aspect-[16/10]" /></Field>
          {t("intro_title", "Community intro title")}
          {t("intro_body", "Community intro text", true)}
        </div>
        <div className="space-y-4">
          {t("founder_name", "Founder name")}
          {t("founder_title", "Founder title")}
          {t("founder_message", "Founder welcome message", true)}
          <Field label="Founder welcome video"><VideoUpload value={f.founder_video_url} onChange={set("founder_video_url")} disabled={!data} /></Field>
          {!(f.founder_video_url || "").startsWith("/api/community/media/") && t("founder_video_url", "…or paste a YouTube / Vimeo link instead")}
          <Field label="Founder photo / video cover"><SingleImageUpload value={f.founder_image} onChange={set("founder_image")} testId="content-founder-image" /></Field>
          <Field label="Memory photos (used until you feature photos on the Community Wall)"><MultiImageUpload value={f.memory_images || []} onChange={set("memory_images")} testId="content-memory-images" /></Field>
        </div>
      </div>
    </Panel>
  );
}

export function ActivityTab() {
  const [rows] = useAdminData("/admin/community/activity");
  return (
    <Panel title="Administrative activity" testId="admin-activity-tab">
      <div className="divide-y divide-[#F0E8DD]">
        {(rows || []).map((a) => (
          <div key={a.id} className="py-3 flex flex-wrap gap-3 text-sm">
            <span className="text-xs text-[#7A6A62] w-36 shrink-0">{fmtDate(a.created_at, { month: "short", day: "numeric" })} {fmtTime(a.created_at)}</span>
            <span className="uppercase text-[10px] tracking-[0.18em] w-24 shrink-0 pt-0.5">{a.action}</span>
            <span className="text-[#7A6A62] w-28 shrink-0">{a.target_type}</span>
            <span className="flex-1">{a.summary}</span>
            <span className="text-xs text-[#7A6A62]">{a.admin}</span>
          </div>
        ))}
        {rows && !rows.length && <p className="text-sm text-[#7A6A62] py-4">No activity yet.</p>}
      </div>
    </Panel>
  );
}
